import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { AlertCircle, Calendar, Filter, History, TrendingDown, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { attendanceService } from '@/services';
import type { AttendanceRecord } from '@/types';
import { formatNaira, formatStatus } from '@/utils';
import { cn } from '@/utils/cn';

interface DeductionRow {
  id: string;
  date: string;
  type: string;
  amount: number;
  reason: string;
  status: 'active' | 'pardoned' | 'deleted';
  pardoned: boolean;
}

export function Deductions() {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  useEffect(() => {
    const fetchDeductions = async () => {
      setLoading(true);
      try {
        const data = await attendanceService.getMyDeductions(
          selectedMonth.year(),
          selectedMonth.month() + 1,
        );
        setRecords(data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchDeductions();
  }, [selectedMonth]);

  const deductionRows: DeductionRow[] = records.flatMap((record) =>
    record.deductions
      .filter((deduction) => !deduction.deletedAt)
      .map((deduction) => ({
        id: deduction._id,
        date: record.date,
        type: deduction.type,
        amount: deduction.amount,
        reason: deduction.reason,
        status: deduction.pardoned ? 'pardoned' : 'active',
        pardoned: deduction.pardoned || false,
      })),
  );

  const unpardonedDeductions = deductionRows.filter((d) => !d.pardoned);
  const totalActive = unpardonedDeductions.reduce((sum, d) => sum + d.amount, 0);
  const pardonedDeductions = deductionRows.filter((d) => d.pardoned);
  const totalPardoned = pardonedDeductions.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-2">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Deductions
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-xs font-medium">
            Monitor salary adjustments and penalty records for the current period.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-3 rounded-xl glass-card dark:glass-card-dark shadow-sm flex items-center gap-3"
      >
        <div className="relative flex-1 md:flex-none">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="month"
            value={selectedMonth.format('YYYY-MM')}
            onChange={(e) => setSelectedMonth(dayjs(e.target.value))}
            className="w-full md:w-64 pl-9 pr-3 py-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-xs font-bold cursor-pointer"
          />
        </div>
        <div className="hidden md:flex items-center gap-1.5 text-slate-400 text-[10px] font-bold uppercase tracking-widest ml-auto">
          <Filter className="w-3.5 h-3.5" />
          Filtering by Month
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          {
            label: 'Total Deductions',
            value: formatNaira(totalActive),
            icon: Wallet,
            color: 'text-rose-500',
            bg: 'bg-rose-500/10',
            desc: 'Unpardoned penalties',
          },
          {
            label: 'Penalty Count',
            value: unpardonedDeductions.length,
            icon: AlertCircle,
            color: 'text-amber-500',
            bg: 'bg-amber-500/10',
            desc: `${deductionRows.length} total recorded`,
          },
          {
            label: 'Removed Fees',
            value: formatNaira(totalPardoned),
            icon: TrendingDown,
            color: 'text-slate-400 dark:text-white/40',
            bg: 'bg-neolab-green/5 dark:bg-white/5',
            desc: 'Pardoned by admin',
          },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.1 }}
            className="p-3 rounded-xl glass-card dark:glass-card-dark shadow-sm hover:shadow-lg transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-2">
              <div className={cn('p-2 rounded-lg', stat.bg)}>
                <stat.icon className={cn('w-4 h-4', stat.color)} />
              </div>
              <div className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                {stat.desc}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-0.5">
                {stat.label}
              </p>
              <p
                className={cn(
                  'text-xl font-black tracking-tight',
                  stat.color.includes('rose') ? 'text-rose-500' : 'text-slate-900 dark:text-white',
                )}
              >
                {stat.value}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden"
      >
        <div className="px-3 py-2 border-b border-neolab-green/5 dark:border-white/5 flex items-center justify-between bg-neolab-green/[0.02] dark:bg-white/5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-white dark:bg-neolab-forest shadow-sm">
              <History className="w-4 h-4 text-rose-500" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                Penalty History
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                {selectedMonth.format('MMMM YYYY')}
              </p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Date
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Category
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Reason
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Status
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center">
                    <div className="flex justify-center">
                      <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </td>
                </tr>
              ) : deductionRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center">
                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Wallet className="w-6 h-6 text-slate-300 dark:text-slate-600" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-bold text-sm">
                      No deductions found for this month
                    </p>
                  </td>
                </tr>
              ) : (
                deductionRows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-neolab-green/[0.02] dark:border-white/5 hover:bg-neolab-green/[0.02] dark:hover:bg-white/5 transition-colors"
                  >
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-neolab-green/10 dark:bg-white/10 flex items-center justify-center font-black text-slate-500 text-xs">
                          {dayjs(row.date).date()}
                        </div>
                        <div>
                          <p className="text-xs font-black text-slate-900 dark:text-white">
                            {dayjs(row.date).format('ddd')}
                          </p>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                            {dayjs(row.date).format('MMM YYYY')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-2">
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider',
                          row.type === 'late_arrival' && 'bg-amber-500/10 text-amber-600',
                          row.type === 'extra_late' && 'bg-rose-500/10 text-rose-600',
                          row.type === 'missed_clock_out' && 'bg-purple-500/10 text-purple-600',
                        )}
                      >
                        {formatStatus(row.type)}
                      </span>
                    </td>
                    <td className="p-2 text-xs font-bold text-slate-600 dark:text-slate-400">
                      {row.reason}
                    </td>
                    <td className="p-2">
                      <div
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider',
                          row.status === 'active'
                            ? 'bg-rose-500/10 text-rose-600'
                            : row.status === 'pardoned'
                              ? 'bg-green-500/10 text-green-600'
                              : 'bg-neolab-green/5 dark:bg-white/5 text-slate-400',
                        )}
                      >
                        <div
                          className={cn(
                            'w-1 h-1 rounded-full',
                            row.status === 'active'
                              ? 'bg-rose-500'
                              : row.status === 'pardoned'
                                ? 'bg-green-500'
                                : 'bg-slate-400',
                          )}
                        />
                        {row.status === 'active'
                          ? 'Active'
                          : row.status === 'pardoned'
                            ? 'Pardoned'
                            : 'Waived'}
                      </div>
                    </td>
                    <td className="p-2 text-right font-black text-rose-500 text-sm">
                      {formatNaira(row.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && deductionRows.length > 0 && (
              <tfoot>
                <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-t border-neolab-green/10 dark:border-white/10">
                  <td
                    colSpan={4}
                    className="p-2 text-xs font-black text-slate-900 dark:text-white uppercase tracking-widest"
                  >
                    Net Payable Deductions
                  </td>
                  <td className="p-2 text-right font-black text-lg text-rose-500">
                    {formatNaira(totalActive)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </motion.div>
    </div>
  );
}

export default Deductions;
